import { computeExportVideoBitrateBps } from '@/rendering/export/adapters/webCodecsExportBitrate'
import type { ExportRenderQualityId } from '@/rendering/export/types'

/** Matroska / WebM 轨 FourCC，与 `webm-muxer` 的 `video.codec` 一致 */
export type WebMVideoMatroskaCodec = 'V_VP9' | 'V_VP8'

/**
 * 经 `VideoEncoder.isConfigSupported` 验证通过的配置摘要。
 */
export type PickedWebCodecsVideoConfig = {
  /** 传入 `VideoEncoder.configure` 的 `codec` 字符串 */
  codec: string
  /** 写入 WebM 的轨 codec 标识 */
  matroskaCodec: WebMVideoMatroskaCodec
  /** 与探测时一致的码率（bps） */
  bitrate: number
  /** 是否编码 alpha 通道（VP9 profile 0/2 支持） */
  alpha: boolean
}

/**
 * 依次尝试的 VP9 codec 字符串（profile / level / bitDepth），高分辨率需更高 level。
 *
 * @see https://www.w3.org/TR/webcodecs-codec-registry/#video-codec-registry
 */
const VP9_CODEC_CANDIDATES: readonly string[] = [
  'vp09.00.10.08',
  'vp09.00.11.08',
  'vp09.00.20.08',
  'vp09.00.21.08',
  'vp09.00.30.08',
  'vp09.00.31.08',
  'vp09.01.20.08',
  'vp09.02.31.08',
  'vp09.03.31.08',
]

/**
 * 带 alpha 的 VP9 候选。注意：VP9 alpha 在 WebCodecs 下由 `VideoEncoder.configure({ alpha: 'keep' })` 启用，
 * 并非所有浏览器 / profile 都支持；此处只保留 profile 0 / 2（支持 alpha 的常见 profile），
 * 不包含 profile 1 / 3（色度子采样不含 alpha 常用档）。
 */
const VP9_CODEC_CANDIDATES_WITH_ALPHA: readonly string[] = [
  'vp09.00.10.08',
  'vp09.00.11.08',
  'vp09.00.20.08',
  'vp09.00.21.08',
  'vp09.00.30.08',
  'vp09.00.31.08',
  'vp09.02.31.08',
]

/**
 * 在指定分辨率、帧率、码率下探测可用的 VP9 / VP8 `VideoEncoder` 配置。
 *
 * - `alpha = false`：VP9 优先，失败回退 VP8。
 * - `alpha = true`：仅尝试 VP9 + `alpha: 'keep'`；VP8 不支持 alpha，直接返回 `null`。
 *
 * @param width - 视频宽
 * @param height - 视频高
 * @param fps - 帧率（用于 `framerate` 元数据）
 * @param quality - 映射目标码率
 * @param alpha - 是否编码透明通道
 * @returns 可用配置；环境不支持或均不支持时返回 `null`
 */
export async function pickWebCodecsWebmVideoConfig(
  width: number,
  height: number,
  fps: number,
  quality: ExportRenderQualityId,
  alpha = false,
): Promise<PickedWebCodecsVideoConfig | null> {
  if (typeof VideoEncoder === 'undefined') return null
  const w = Math.max(1, Math.round(width))
  const h = Math.max(1, Math.round(height))
  const fpsClamped = Math.min(120, Math.max(1, Math.round(fps)))
  const bitrate = computeExportVideoBitrateBps(w, h, quality)
  const candidates = alpha ? VP9_CODEC_CANDIDATES_WITH_ALPHA : VP9_CODEC_CANDIDATES

  for (const codec of candidates) {
    try {
      const { supported, config } = await VideoEncoder.isConfigSupported({
        codec,
        width: w,
        height: h,
        bitrate,
        framerate: fpsClamped,
        ...(alpha ? { alpha: 'keep' as const } : {}),
      })
      const codecOut = config?.codec
      if (supported && codecOut) {
        return { codec: codecOut, matroskaCodec: 'V_VP9', bitrate, alpha }
      }
    } catch {
      /** 部分浏览器对不支持的 profile 抛错而非返回 `{ supported: false }` */
    }
  }

  if (alpha) return null

  const vp8 = await VideoEncoder.isConfigSupported({
    codec: 'vp8',
    width: w,
    height: h,
    bitrate,
    framerate: fpsClamped,
  })
  const vp8Codec = vp8.config?.codec
  if (vp8.supported && vp8Codec) {
    return { codec: vp8Codec, matroskaCodec: 'V_VP8', bitrate, alpha: false }
  }

  return null
}
